import { Trash, Check, Printer } from "lucide-react"
import { Button } from "@components/common/button"
import { Card, CardDescription, CardHeader, CardTitle } from "@components/common/card"
import {
  APPROVE_ALL_INITIAL_BOQ_ENDPOINT,
  DELETE_MAIN_BOQ_ENDPOINT,
  FINANCIAL_SERVER_URL,
  GET_ESTIMATE_BOQ_ENDPOINT,
  GET_MAIN_BOQ_ENDPOINT,
} from "@pma/utils/api-routes"
import axios from "axios"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useMutation, useQuery } from "@tanstack/react-query"
import useTranslator from "@pma/hooks/use-translator"
import { useNavigate, useSearchParams } from "react-router-dom"
import { PROJECTS_PAGE_ROUTE } from "@pma/utils/page-routes"
import { format, formatDistance } from "date-fns"
import { he } from "date-fns/locale"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { toast } from "@pma/hooks/use-toast"
import PmsLoader from "@components/common/loader"
import { Badge } from "@components/common/badge"
import { DictType } from "@pma/types"
import UnauthorizedCard from "@components/layout/unauthorized-card"
import MainBoqTabs from "./main-boq-tabs"
import { cn, getFinancialErrorMessage } from "@pma/utils/helpers"
import BoQInfo from "./bill-details"
import { useState } from "react"
import { FinancialTooltip } from "./financial-glossary"
import MainBillOfQuantitiesPrint from "./main-bill-of-quantities-print"

interface Props {
  boqId: string
  inProject?: boolean
  closeDialog?: CallableFunction
}

export function BoQDetails({ boqId, inProject, closeDialog }: Props) {
  const { translate, locale } = useTranslator()

  const navigate = useNavigate()
  const [searchParam] = useSearchParams()

  const newParams = new URLSearchParams(searchParam)

  const isHebrew = locale === "he"

  const accessToken = parseCookies()[ACCESS_TOKEN]
  const [mainBillOfQuantitiesPrintDialog, setMainBillOfQuantitiesPrintDialog] = useState(false)

  const getMainBoQDetails = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + GET_MAIN_BOQ_ENDPOINT,
      {
        boq_id: boqId,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const {
    data: mainBoQDetails,
    isLoading: isLoadingMainBoQs,
    refetch: refetchMainBoQs,
    isError,
    error,
  } = useQuery({
    queryKey: ["main-boq", boqId],
    queryFn: getMainBoQDetails,
  })

  const getEstimateBoQDetails = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + GET_ESTIMATE_BOQ_ENDPOINT,
      {
        boq_estimate_id: mainBoQDetails?.boq?.estimate_boq_id,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const {
    data: estimateBoQDetails,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["estimate-boq", boqId],
    queryFn: getEstimateBoQDetails,
    enabled: mainBoQDetails?.boq?.estimate_boq_id ? true: false
  })

  const boq = mainBoQDetails?.boq

  const deleteMainBoQ = async () => {
    const response = await axios.delete(
      FINANCIAL_SERVER_URL + DELETE_MAIN_BOQ_ENDPOINT + `/${boqId}`,
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const approveMainBoQ = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + APPROVE_ALL_INITIAL_BOQ_ENDPOINT,
      {
        boq_id: boqId,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const handleDeleteBoQMutation = useMutation({
    mutationKey: ["delete-boq", boqId],
    mutationFn: deleteMainBoQ,
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("BoQ Deleted Successfully"),
        variant: "success",
      })

      newParams.delete("main")
      closeDialog && closeDialog()
      refetchMainBoQs()

      if (inProject) {
        return navigate(`${PROJECTS_PAGE_ROUTE}/${boq?.project_id}`)
      }

      navigate(`?${newParams.toString()}`)
    },
    onError: (error) => {
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "delete boq", translate),
        variant: "destructive",
      })
    },
  })

  const handleApproveBoQMutation = useMutation({
    mutationKey: ["approve-boq"],
    mutationFn: approveMainBoQ,
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("BoQ Approved Successfully"),
        variant: "success",
      })
      refetchMainBoQs()
    },
    onError: (error) => {
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "delete boq", translate),
        variant: "destructive",
      })
    },
  })

  const boqError = error as DictType

  return (
    <div className={cn("space-y-6", isHebrew ? "rtl" : "ltr")}>
      {isError ? (
        <div className='flex w-full flex-col items-center justify-center'>
          <div className='flex items-center'>
            <UnauthorizedCard message={boqError?.response?.data?.message} />
          </div>
        </div>
      ) : (
        <>
          {isLoadingMainBoQs ? (
            <Card className='flex w-full items-center justify-center'>
              <PmsLoader />
            </Card>
          ) : (
            <Card>
              <CardHeader className='w-full pb-3'>
                <div className='flex flex-col gap-4 md:flex-row md:items-start md:justify-between'>
                  <div className='w-full'>
                    <CardTitle className='text-xl capitalize'>
                      <FinancialTooltip term="boq">{boq?.name}</FinancialTooltip>
                    </CardTitle>
                    <CardDescription className='mt-1'>
                      <div className='flex flex-col gap-1'>
                        <BoQInfo
                          boq={boq}
                          projectId={boq?.project_id}
                          contractorId={boq?.company_id}
                        />
                        <div className='flex items-center gap-2'>
                          {translate("Status")}:
                          <Badge
                            className='font-medium'
                            variant={boq?._not_approved ? "default" : "secondary"}
                          >
                            {boq?._not_approved ? translate("Pending") : translate("Approved")}{" "}
                          </Badge>
                        </div>

                        <div className='flex items-center gap-2'>
                          {translate("Paid Amount")}:<span>{boq?.paid}</span>
                        </div>

                        <div className='flex w-full items-center justify-end'>
                          <div className='flex items-center gap-2'>
                            {boq?._not_approved && (
                              <Button
                                rightSection={<Check size={12} />}
                                size={"sm"}
                                variant={"ghost"}
                                onClick={() => handleApproveBoQMutation.mutate()}
                                className='font-medium text-primary'
                              >
                                {translate("Approve")}
                              </Button>
                            )}

                            <Button
                              variant={"outline"}
                              size={"sm"}
                              onClick={() => setMainBillOfQuantitiesPrintDialog(true)}
                            >
                              <Printer className={`mx-1 h-4 w-4`} />
                              <span className='hidden sm:inline'>{translate("Print")}</span>
                            </Button>

                            <Button
                              rightSection={<Trash size={12} />}
                              size={"sm"}
                              variant={"ghost"}
                              onClick={() => {
                                if (!window.confirm(translate("are you sure you want to delete this boq?"))) return
                                handleDeleteBoQMutation.mutate()
                              }}
                              className='font-medium text-destructive'
                            >
                              {translate("Delete")}
                            </Button>
                          </div>

                          <div className='flex items-center gap-2 text-xs'>
                            {translate("Created")}:
                            <Tooltip>
                              <TooltipTrigger>
                                <span className='font-medium'>
                                  {boq?.created_at &&
                                    formatDistance(boq?.created_at, new Date(), {
                                      locale: isHebrew ? he : undefined,
                                      addSuffix: true,
                                    })}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                {boq?.created_at && format(boq?.created_at, "dd/MM/yyyy")}
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>
          )}

          {!isLoadingMainBoQs && <MainBoqTabs boq={boq} />}
        </>
      )}

      {mainBillOfQuantitiesPrintDialog && (
        <MainBillOfQuantitiesPrint
          isLoading={isLoading}
          data={mainBoQDetails?.boq_items}
          mainBillOfQuantitiesPrintDialog={mainBillOfQuantitiesPrintDialog}
          setMainBillOfQuantitiesPrintDialog={setMainBillOfQuantitiesPrintDialog}
        />
      )}
    </div>
  )
}
